import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const CameraHeatmapViewer = () => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  // const source = JSON.parse(localStorage.getItem('source'));
  const source = "trends";
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!source) {
      setError('Source ID is missing. Please select a store first.');
      setLoading(false);
      return;
    }

    const fetchCameras = async () => {
      try {
        setLoading(true);
        console.log('Fetching cameras for source:', source);

        const response = await axios.post('https://disha-pocket.neophyte.live/trends-api/api/sentinel/getAllCameras', {
          source: source
        });

        if (response.data && response.data.cameras) {
          console.log('Cameras data received:', response.data.cameras);
          setCameras(response.data.cameras);

          // Set first camera as selected and fetch its heatmap data
          if (response.data.cameras.length > 0) {
            const firstCamera = response.data.cameras[0];
            setSelectedCamera(firstCamera);
            fetchHeatmapData(firstCamera);
          }
        } else {
          setError('No cameras found for this store');
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch camera data');
        setLoading(false);
        console.error('Error fetching cameras:', err);
      }
    };

    fetchCameras();
  }, [source]);

  const handleCameraClick = (camera) => {
    setSelectedCamera(camera);
    fetchHeatmapData(camera);
  };

  // Get date and time from Redux
  const date = useSelector((state) => state.customization.currentDate);
  const selectedTime = useSelector((state) => state.customization.selectedTime);

  // Fetch heatmap data when date or time range changes
  useEffect(() => {
    if (selectedCamera) {
      fetchHeatmapData(selectedCamera);
    }
  }, [date, selectedTime]);

  const fetchHeatmapData = async (camera) => {
    if (!camera || !source) return;

    try {
      setHeatmapLoading(true);

      // Use dynamic values from Redux
      const response = await axios.post('https://disha-pocket.neophyte.live/trends-api/api/sentinel/heatmap', {
        date: date ? date.split('T')[0] : new Date().toISOString().split('T')[0],
        source_id: source,
        start_time: selectedTime?.start_time || new Date().toUTCString(),
        end_time: selectedTime?.end_time || new Date(Date.now() + 15 * 60 * 1000).toUTCString(),
        camera: camera.device_name
      });

      if (response.data && response.data.data && response.data.data.length > 0) {
        setHeatmapData(response.data.data[0]);
      } else {
        setHeatmapData(null);
        console.error('No heatmap data found for this camera');
      }
    } catch (err) {
      console.error('Error fetching heatmap data:', err);
      setHeatmapData(null);
    } finally {
      setHeatmapLoading(false);
    }
  };

  useEffect(() => {
    if (heatmapData && selectedCamera) {
      drawHeatmap();
    }
  }, [heatmapData, selectedCamera]);

  const drawHeatmap = () => {
    if (!heatmapData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Set canvas dimensions to match the image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the original image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Create a temporary canvas for the heatmap overlay
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');

      // Add a base layer of very light heat across the entire canvas to create a more continuous effect
      tempCtx.fillStyle = 'rgba(0, 0, 255, 0.05)';
      tempCtx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw heatmap points on the temporary canvas
      if (heatmapData.points && heatmapData.points.length > 0) {
        // Get values for normalization
        const values = heatmapData.points.map(point => point.value);
        const maxValue = Math.max(...values);
        const minValue = Math.min(...values);
        console.log('Heatmap values - Min:', minValue, 'Max:', maxValue);

        // Draw each point with a gradient based on its value
        heatmapData.points.forEach(point => {
          // Use logarithmic scaling for better visualization of skewed data
          // Add 1 to avoid log(0) and to ensure even value=1 gets some visibility
          const logValue = Math.log(point.value + 1) / Math.log(maxValue + 1);

          // Enhanced normalization that gives more visibility to lower values
          // This uses a square root transformation to boost lower values
          const normalizedValue = Math.sqrt(logValue);

          // Adjust radius calculation to ensure even small values get reasonable visibility
          // Even larger radius range for an extremely spread-out heatmap
          // Using very large radii to create extensively overlapping areas
          const radius = Math.min(Math.max(normalizedValue * 150, 40), 150);

          console.log(`Point (${point.x}, ${point.y}) - Value: ${point.value}, Normalized: ${normalizedValue.toFixed(3)}, Radius: ${radius.toFixed(1)}`);

          // Create a radial gradient
          const gradient = tempCtx.createRadialGradient(
            point.x, point.y, 0,
            point.x, point.y, radius
          );

          // Determine the primary color based on the normalized value
          // This will be the center color of the gradient - using more vibrant colors
          let primaryColor;
          if (normalizedValue < 0.25) {
            // Vibrant blue for lowest values
            primaryColor = { r: 0, g: 80, b: 255, name: 'blue' };
          } else if (normalizedValue < 0.5) {
            // Vibrant green for low-medium values
            primaryColor = { r: 0, g: 230, b: 0, name: 'green' };
          } else if (normalizedValue < 0.75) {
            // Vibrant yellow for medium-high values
            primaryColor = { r: 255, g: 230, b: 0, name: 'yellow' };
          } else {
            // Vibrant red for highest values
            primaryColor = { r: 255, g: 30, b: 0, name: 'red' };
          }

          // Define all colors in the hierarchy for gradient creation with more vibrant colors
          const colorHierarchy = {
            red: { r: 255, g: 30, b: 0 },
            yellow: { r: 255, g: 230, b: 0 },
            green: { r: 0, g: 230, b: 0 },
            blue: { r: 0, g: 80, b: 255 }
          };

          // Create a hierarchical gradient based on the primary color
          // The gradient will start with the primary color and transition through the hierarchy

          // Start with the primary color at the center (with higher opacity for more vibrancy)
          gradient.addColorStop(0, `rgba(${primaryColor.r}, ${primaryColor.g}, ${primaryColor.b}, 0.9)`);

          // Create the gradient stops based on the hierarchy (red→yellow→green→blue)
          if (primaryColor.name === 'red') {
            // Red → Yellow → Green → Blue
            gradient.addColorStop(0.3, `rgba(${colorHierarchy.yellow.r}, ${colorHierarchy.yellow.g}, ${colorHierarchy.yellow.b}, 0.7)`);
            gradient.addColorStop(0.6, `rgba(${colorHierarchy.green.r}, ${colorHierarchy.green.g}, ${colorHierarchy.green.b}, 0.5)`);
            gradient.addColorStop(0.85, `rgba(${colorHierarchy.blue.r}, ${colorHierarchy.blue.g}, ${colorHierarchy.blue.b}, 0.3)`);
          } else if (primaryColor.name === 'yellow') {
            // Yellow → Green → Blue
            gradient.addColorStop(0.4, `rgba(${colorHierarchy.green.r}, ${colorHierarchy.green.g}, ${colorHierarchy.green.b}, 0.6)`);
            gradient.addColorStop(0.8, `rgba(${colorHierarchy.blue.r}, ${colorHierarchy.blue.g}, ${colorHierarchy.blue.b}, 0.4)`);
          } else if (primaryColor.name === 'green') {
            // Green → Blue
            gradient.addColorStop(0.6, `rgba(${colorHierarchy.blue.r}, ${colorHierarchy.blue.g}, ${colorHierarchy.blue.b}, 0.5)`);
          }
          // Blue has no further transitions in the hierarchy

          gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

          tempCtx.beginPath();
          tempCtx.fillStyle = gradient;
          tempCtx.arc(point.x, point.y, radius, 0, Math.PI * 2);
          tempCtx.fill();
        });
      }

      // Apply an even stronger blur effect to create a very spread-out, continuous heatmap
      tempCtx.filter = 'blur(35px)';
      tempCtx.globalCompositeOperation = 'source-over';
      tempCtx.drawImage(tempCanvas, 0, 0);
      tempCtx.filter = 'none';

      // Overlay the heatmap on the original image with slightly increased opacity
      ctx.globalAlpha = 0.8;
      ctx.drawImage(tempCanvas, 0, 0);
      ctx.globalAlpha = 1.0;
    };

    img.src = heatmapData.frame;
  };

  if (loading) {
    return (
      <div
        className="camera-loading-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '570px',
          flexDirection: 'column'
        }}
      >
        <div
          className="spinner"
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderLeftColor: '#000',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}
        ></div>
        <style>{`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
        <div style={{ fontWeight: '500', color: '#555' }}>Loading camera data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="error-container"
        style={{
          color: '#d32f2f',
          padding: '16px',
          border: '1px solid #ffcdd2',
          borderRadius: '8px',
          backgroundColor: '#ffebee',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginRight: '12px' }}
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        {error}
      </div>
    );
  }

  return (
    <div className="camera-heatmap-container" style={{ maxWidth: '100%', margin: '0 auto' }}>
      <div className="camera-heatmap-layout" style={{ display: 'flex', gap: '24px' }}>
        {/* Left side - Camera list */}
        <div
          className="camera-list-container"
          style={{
            width: '25%',
            minWidth: '200px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            padding: '16px',
            backgroundColor: '#fff',
            height: 'fit-content'
          }}
        >
          <div
            className="camera-list-header"
            style={{
              fontWeight: '600',
              marginBottom: '16px',
              color: '#1a1a1a',
              fontSize: '0.95rem'
            }}
          >
            Select Camera
          </div>

          <div
            className="camera-list h-full"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '500px',
              overflowY: 'auto',
              padding: '4px'
            }}
          >
            {cameras.map((camera) => (
              <button
                key={camera._id}
                onClick={() => handleCameraClick(camera)}
                className="camera-button"
                style={{
                  padding: '14px 16px',
                  height: '50px',
                  borderRadius: '6px',
                  color: 'white',
                  backgroundColor: selectedCamera && selectedCamera._id === camera._id ? '#44BE54' : '#64748b',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  lineHeight: '1.2',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  transition: 'all 0.2s ease',
                  fontWeight: selectedCamera && selectedCamera._id === camera._id ? '500' : '400',
                  boxShadow: selectedCamera && selectedCamera._id === camera._id ? '0 2px 5px rgba(37, 99, 235, 0.3)' : 'none'
                }}
              >
                {camera.device_name}
              </button>
            ))}
          </div>
        </div>

        {/* Right side - Heatmap display */}
        <div
          className="heatmap-display-container"
          style={{
            width: '75%',
            minWidth: '300px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            // padding: '20px',
            backgroundColor: '#fff'
          }}
        >
          {selectedCamera ? (
            <div className="selected-camera-content">
              <div
                className="heatmap-image-container flex justify-center items-center"
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '8px',
                  maxWidth: 'auto',
                  backgroundColor: '#f9fafb',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {heatmapLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '550px',
                      width: '100%'
                    }}
                  >
                    <div
                      className="spinner"
                      style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '50%',
                        borderLeftColor: '#000',
                        animation: 'spin 1s linear infinite'
                      }}
                    ></div>
                  </div>
                ) : heatmapData ? (
                  <TransformWrapper
                    initialScale={1}
                    initialPositionX={0}
                    initialPositionY={0}
                    minScale={0.1}
                    maxScale={4}
                    wheel={{ step: 0.1 }}
                    doubleClick={{ disabled: false }}
                    zoomAnimation={{ disabled: false }}
                    panning={{ disabled: false }}
                    centerOnInit={true}
                    centerZoomedOut={true}
                  >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                      <>
                        <div className="zoom-controls" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                          <button
                            onClick={() => zoomIn()}
                            style={{
                              margin: '0 5px',
                              padding: '5px 10px',
                              backgroundColor: '#4f46e5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            +
                          </button>
                          <button
                            onClick={() => zoomOut()}
                            style={{
                              margin: '0 5px',
                              padding: '5px 10px',
                              backgroundColor: '#4f46e5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            -
                          </button>
                          <button
                            onClick={() => resetTransform()}
                            style={{
                              margin: '0 5px',
                              padding: '5px 10px',
                              backgroundColor: '#4f46e5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Reset
                          </button>
                        </div>
                        <TransformComponent
                          wrapperStyle={{
                            width: '100%',
                            height: '550px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            overflow: 'hidden'
                          }}
                          contentStyle={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            margin: 'auto'
                          }}>
                            <canvas
                              ref={canvasRef}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '550px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                display: 'block',
                                margin: 'auto'
                              }}
                            />
                          </div>
                        </TransformComponent>
                      </>
                    )}
                  </TransformWrapper>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '550px',
                      width: '100%'
                    }}
                  >
                    <p style={{ color: '#64748b', fontWeight: '500' }}>No heatmap data available for this camera</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              className="heatmap-placeholder"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px dashed #cbd5e1'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginBottom: '16px' }}
              >
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
              <p style={{ color: '#64748b', fontWeight: '500' }}>Select a camera to view its heatmap</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraHeatmapViewer;
